// App.js  — XFactualApp shell
import React, { useState, useEffect } from 'react';
import {
  NavLink,
  Routes,
  Route,
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  ThemeProvider,
  CssBaseline,
  createTheme,
  Box,
  Divider
} from '@mui/material';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import LogoutIcon from '@mui/icons-material/Logout';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InsightsIcon from '@mui/icons-material/Insights';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import PrivacyPolicy from './components/Policy';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import HowToUsePage from './pages/HowToUsePage';
import XFactualAppContent from './components/XFactualApp';
import AdvancedFeaturesPage from './pages/AdvancedFeaturesPage';
import ContactPage from './pages/ContactPage';

const appBarHeight = 64; // must match Header AppBar height

export default function App() {
  const [darkMode, setDarkMode] = useState(false);
  const toggleDarkMode = () => setDarkMode((p) => !p);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = () => setDrawerOpen((p) => !p);

  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('xfactual_user');
    if (storedUser) setUser(JSON.parse(storedUser));
  }, [location]);

  const theme = createTheme({
    palette: { mode: darkMode ? 'dark' : 'light' },
    typography: {
      fontFamily: `'IBM Plex Sans Condensed', 'Roboto', 'Helvetica', 'Arial', sans-serif`,
    },
  });

  const handleLogout = () => {
    localStorage.removeItem('xfactual_user');
    localStorage.removeItem('xfactual_id');
    setUser(null);
    navigate('/login');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Root layout: header + main (flex:1) + footer */}
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Global Header (likely position="fixed") */}
        <Header
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          toggleDrawer={toggleDrawer}
        />

        {/* Drawer / Sidebar */}
        <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
          <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer}>
            <List>
              <ListItemButton component={RouterLink} to="/">
                <ListItemIcon><HomeIcon /></ListItemIcon>
                <ListItemText primary="Home" />
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/about"
                sx={{
                  '&.active': {
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                  },
                }}
              >
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText primary="About" />
              </ListItemButton>

              <ListItemButton component={RouterLink} to="/XFactualApp">
                <ListItemIcon><FactCheckIcon /></ListItemIcon>
                <ListItemText primary="Fact Checking" />
              </ListItemButton>

              <ListItemButton component={RouterLink} to="/howtouse">
                <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
                <ListItemText primary="How to Use" />
              </ListItemButton>

              <ListItemButton component={RouterLink} to="/advancedfeaturespage">
                <ListItemIcon><InsightsIcon /></ListItemIcon>
                <ListItemText primary="Advanced Features" />
              </ListItemButton>

              <ListItemButton component={RouterLink} to="/contact">
                <ListItemIcon><MailOutlineIcon /></ListItemIcon>
                <ListItemText primary="Contact" />
              </ListItemButton>

              <Divider />

              {user && (
                <ListItemButton onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon /></ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              )}
            </List>
          </Box>
        </Drawer>

        {/* MAIN CONTENT (flex: 1 keeps footer at bottom) */}
   <Box
   component="main"
   sx={{
     flex: 1,
     display: 'flex',
     flexDirection: 'column',
     pt: `${appBarHeight + 8}px`,
     px: 2,
     pb: 4, // breathing room above footer
   }}
 >
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />

            {/* Auth */}
            <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
            <Route path="/signup" element={user ? <Navigate to="/" /> : <SignUpPage />} />

            {/* Fact Checking */}
            <Route
              path="/XFactualApp"
              element={
                <XFactualAppContent
                  darkMode={darkMode}
                  toggleDarkMode={toggleDarkMode}
                  toggleDrawer={toggleDrawer}
                />
              }
            />

            <Route path="/howtouse" element={<HowToUsePage />} />
            <Route path="/advancedfeaturespage" element={<AdvancedFeaturesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
        </Box>

        {/* GLOBAL FOOTER — always at bottom */}
        <Footer darkMode={darkMode} />
      </Box>
    </ThemeProvider>
  );
}

